/**
 * Navigation rail (#92): one entry per registered module, rendered from
 * the module registry. Active state follows the router, never a store.
 */

import { BaseComponent, type BaseComponentOptions } from './base-component';
import { escapeHtml } from '../core/utilities/dom';
import {
  MODULE_ICONS,
  ACTION_ICONS,
  type ActionIcon,
} from '../web/shell/icons';
import { defaultPath, isActiveRoute } from '../web/shell/resolve-route';
import type { AppModule } from '../web/shell/module.types';
import type { Router } from '../web/shell/router';

/**
 * A rail action (#92 follow-up chrome cleanup): not a module, not on the
 * dashboard — e.g. Log out, rendered as a real <button> at the bottom of
 * the rail, visually separated from the module items.
 */
export interface NavRailAction {
  id: string;
  labelKey: string;
  icon: ActionIcon;
  onSelect: () => void;
}

export interface NavRailOptions extends BaseComponentOptions {
  modules: readonly AppModule[];
  router: Router;
  actions?: readonly NavRailAction[];
}

class NavRail extends BaseComponent {
  private readonly modules: readonly AppModule[];
  private readonly router: Router;
  private readonly actions: readonly NavRailAction[];
  private unsubscribe: (() => void) | null = null;

  constructor(options: NavRailOptions) {
    super(options);
    this.modules = options.modules;
    this.router = options.router;
    this.actions = options.actions ?? [];
  }

  protected getDefaultCssClass(): string {
    return 'nav-rail';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return { 'shell.navLabel': 'Modules' };
  }

  protected override render(): void {
    if (!this.container) return;
    const items = this.modules
      .map((module) => this.renderItem(module))
      .join('');
    const actions = this.renderActions();
    this.container.innerHTML = `<nav class="${this.baseCssClass}" aria-label="${escapeHtml(this.t('shell.navLabel'))}">${items}${actions}</nav>`;
    this.setActivePath(this.router.current());
    this.bindActions();
  }

  private renderItem(module: AppModule): string {
    const label = escapeHtml(this.t(module.labelKey));
    const body = `${MODULE_ICONS[module.icon]}<span class="${this.baseCssClass}__label">${label}</span>`;
    if (!module.enabled) {
      return `<span class="${this.baseCssClass}__item ${this.baseCssClass}__item--disabled" data-module="${module.id}" aria-disabled="true">${body}</span>`;
    }
    return `<a class="${this.baseCssClass}__item" data-module="${module.id}" href="#${defaultPath(module)}">${body}</a>`;
  }

  /** Actions (e.g. Log out): a real <button>, last, visually separated. */
  private renderActions(): string {
    if (this.actions.length === 0) return '';
    const items = this.actions
      .map((action) => this.renderAction(action))
      .join('');
    return `<div class="${this.baseCssClass}__actions">${items}</div>`;
  }

  private renderAction(action: NavRailAction): string {
    const label = escapeHtml(this.t(action.labelKey));
    const body = `${ACTION_ICONS[action.icon]}<span class="${this.baseCssClass}__label">${label}</span>`;
    return `<button type="button" class="${this.baseCssClass}__item ${this.baseCssClass}__action" data-action="${escapeHtml(action.id)}">${body}</button>`;
  }

  private bindActions(): void {
    if (this.actions.length === 0 || !this.container) return;
    // Match on the parsed `dataset.action` value rather than interpolating
    // action.id into a CSS selector string (which would need CSS.escape —
    // not available in every runtime — to stay safe/valid for any id).
    const buttons = this.container.querySelectorAll<HTMLElement>(
      `.${this.baseCssClass}__action`
    );
    for (const button of buttons) {
      const action = this.actions.find((a) => a.id === button.dataset.action);
      if (action) {
        button.addEventListener('click', () => action.onSelect());
      }
    }
  }

  protected override onInitialize(): void {
    this.unsubscribe = this.router.onRouteChange((path) =>
      this.setActivePath(path)
    );
  }

  private setActivePath(path: string | null): void {
    for (const module of this.modules) {
      const item = this.container?.querySelector(
        `[data-module="${module.id}"]`
      );
      if (!item) continue;
      if (path !== null && isActiveRoute(module.route, path)) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    }
  }

  override destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    super.destroy();
  }
}

export default NavRail;
